/**
 * GamerPermissionScreen - Camera and microphone permission request for gamers
 * Simplified version of WherebyFlowContainer without team selection
 * Enhanced to handle declined permissions with better error handling
 */

import React from 'react'
import { Box } from '@mui/material'
import { INTERAX_COLORS } from '../../../constants/colors'
import { usePermissionState } from '../hooks/usePermissionState'
import { detectBrowser } from '../helpers/browserDetection'
import {
  PermissionStatusIcon,
  PermissionTitle,
  PermissionDescription,
  BrowserInstructions,
  PermissionActionButtons,
  PermissionLoadingState
} from './permissionScreen'

interface GamerPermissionScreenProps {
  onPermissionsGranted: () => void
  className?: string
}

export const GamerPermissionScreen: React.FC<GamerPermissionScreenProps> = ({
  onPermissionsGranted,
  className = ''
}) => {
  const browser = detectBrowser()
  const {
    permissionState,
    errorMessage,
    isRequestingPermissions,
    hasCheckedPermissions,
    handleRequestPermissions,
    handleCheckAgain
  } = usePermissionState({ onPermissionsGranted })

  // Show loading while checking existing permissions
  if (!hasCheckedPermissions || permissionState === 'checking') {
    return <PermissionLoadingState />
  }

  const isDenied = permissionState === 'denied'
  const hasError = permissionState === 'error' || Boolean(errorMessage)

  return (
    <Box
      className={className}
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        borderRadius: '10px',
        backgroundColor: `${INTERAX_COLORS.heritageBlue}66`,
        gap: 3
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          maxWidth: '500px'
        }}
      >
        <PermissionStatusIcon permissionState={permissionState} hasError={hasError} />
        <PermissionTitle permissionState={permissionState} hasError={hasError} />
        <PermissionDescription permissionState={permissionState} hasError={hasError} errorMessage={errorMessage} />
        {isDenied && <BrowserInstructions browser={browser} />}
        <PermissionActionButtons
          permissionState={permissionState}
          hasError={hasError}
          isRequestingPermissions={isRequestingPermissions}
          onRequestPermissions={handleRequestPermissions}
          onCheckAgain={handleCheckAgain}
        />
      </Box>
    </Box>
  )
}
